import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Play, ArrowLeftCircle, AppWindow } from "lucide-react";

/**
 * "Petit rappel" dialog — shown once per session, between the participant's
 * info submit and the CTA step, to remind them of the 3 key steps:
 *   1. Complete the external action (Facebook follow, Google review, …)
 *   2. Come back to the previous page or to the contest tab
 *   3. Play / watch the draw result
 *
 * Uses sessionStorage to avoid showing it twice if the user clicks on
 * several CTAs in a row.
 */
const STORAGE_KEY = "qrise_petit_rappel_seen";

function actionLabelFor(cta, merchantName, t, i18n) {
  const key = `play.rappel_step1_${cta.type}`;
  if (i18n.exists(key)) {
    return t(key, { merchant: merchantName || t("landing.hero_line1") });
  }
  return cta.label || t("play.rappel_step1_fallback");
}

export default function PetitRappelDialog({
  open,
  ctas = [],
  gameType = "spin_wheel",
  merchantName = "",
  onContinue,
}) {
  const { t, i18n } = useTranslation();
  const [dontShowAgain, setDontShowAgain] = useState(true);

  const requiredFirst = [...ctas].sort(
    (a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0)
  );
  const primaryCta = requiredFirst[0];
  const step1 = primaryCta
    ? actionLabelFor(primaryCta, merchantName, t, i18n)
    : t("play.rappel_step1_fallback");

  // For games with a delayed outcome we say "watch the draw", otherwise
  // "play and try my luck".
  const isDraw = [
    "lottery_draw",
    "prediction_match",
    "guess_price",
  ].includes(gameType);
  const step3 = isDraw
    ? t("play.rappel_step3_draw")
    : t("play.rappel_step3_instant");

  function handleContinue() {
    try {
      if (dontShowAgain) sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* sessionStorage can be disabled in some private modes */
    }
    onContinue?.();
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-md overflow-hidden border-white/10 bg-[#0a0a0e] p-0 text-white sm:rounded-3xl"
        data-testid="petit-rappel-dialog"
      >
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-24"
          style={{
            background:
              "radial-gradient(60% 100% at 50% 0%, rgba(251,191,36,0.25), transparent 70%)",
          }}
        />

        <DialogHeader className="px-6 pb-2 pt-8 text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1">
            <Sparkles className="h-3.5 w-3.5 text-amber-300" strokeWidth={2.5} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-300">
              {t("play.rappel_eyebrow")}
            </span>
          </div>
          <DialogTitle className="mt-3 text-center text-3xl font-black leading-tight tracking-tight text-white">
            {t("play.rappel_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-2">
          <ol className="space-y-4">
            <RappelStep n={1}>
              <span dangerouslySetInnerHTML={{ __html: step1 }} />
            </RappelStep>
            <RappelStep n={2}>
              <span>{t("play.rappel_step2")}</span>
              <div className="mt-2 flex items-center gap-4 text-[11px] text-white/60">
                <span className="inline-flex items-center gap-1.5">
                  <ArrowLeftCircle className="h-3.5 w-3.5" />
                  {t("play.rappel_step2_hint_back")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <AppWindow className="h-3.5 w-3.5" />
                  {t("play.rappel_step2_hint_tabs")}
                </span>
              </div>
            </RappelStep>
            <RappelStep n={3}>{step3}</RappelStep>
          </ol>

          <label className="mt-5 flex cursor-pointer items-center gap-2 text-[12px] text-white/70">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="h-3.5 w-3.5 accent-amber-400"
              data-testid="rappel-dont-show-again"
            />
            {t("play.rappel_dont_show_again")}
          </label>
        </div>

        <div className="px-6 pb-6 pt-3">
          <Button
            type="button"
            onClick={handleContinue}
            className="relative h-12 w-full overflow-hidden rounded-2xl border border-amber-200/40 bg-gradient-to-b from-amber-400 to-amber-500 text-sm font-black uppercase tracking-[0.18em] text-black shadow-[0_10px_24px_-6px_rgba(251,191,36,0.5)] hover:brightness-105"
            data-testid="rappel-continue-btn"
          >
            {t("play.rappel_continue")}
            <Play className="ml-2 h-4 w-4 fill-current" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RappelStep({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <div
        className="relative flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-base font-black text-black"
        aria-hidden
      >
        {n}
      </div>
      <div className="pt-0.5 text-[15px] font-semibold leading-snug text-white [&_b]:font-extrabold [&_b]:text-amber-200">
        {children}
      </div>
    </li>
  );
}

PetitRappelDialog.shouldShow = function shouldShow() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) !== "1";
  } catch (e) {
    return true;
  }
};
